import styled from "@emotion/styled";
import { MDSBubbleAlignment, MDSBubbleVariant } from "./bubble.types";
import { Theme } from "@emotion/react";

// Background color functions
const getBackgroundColor = (variant: MDSBubbleVariant, theme: Theme): string => {
  switch (variant) {
    case MDSBubbleVariant.LightBlue:
      return theme.colors.mblue.x50;
    case MDSBubbleVariant.Green:
      return theme.colors.green.x500;
    case MDSBubbleVariant.DarkGrey:
      return theme.colors.grey.x500;
    case MDSBubbleVariant.LightGrey:
      return theme.colors.grey.x50;
    case MDSBubbleVariant.Grey:
      return theme.colors.grey.x100;
    case MDSBubbleVariant.Image:
      return theme.colors.grey.x100;
    case MDSBubbleVariant.VoiceSelf:
      return theme.colors.background.voice.self;
    case MDSBubbleVariant.VoiceOthers:
      return theme.colors.background.voice.others;
    case MDSBubbleVariant.Transparent:
      return "transparent";
    case MDSBubbleVariant.MessageUser:
      return theme.colors.background.message.user;
  }
};

// Text color functions
const getTextColor = (variant: MDSBubbleVariant, theme: Theme): string => {
  switch (variant) {
    case MDSBubbleVariant.Green:
    case MDSBubbleVariant.DarkGrey:
      return theme.colors.white;
    case MDSBubbleVariant.LightBlue:
    case MDSBubbleVariant.LightGrey:
      return theme.colors.grey.x600;
    case MDSBubbleVariant.Grey:
      return theme.colors.grey.x875;
    case MDSBubbleVariant.Image:
      return theme.colors.grey.x875;
    case MDSBubbleVariant.Transparent:
      return theme.colors.chat.header;
    case MDSBubbleVariant.MessageUser:
    case MDSBubbleVariant.VoiceSelf:
      return theme.colors.text.primary;
    case MDSBubbleVariant.VoiceOthers:
      return theme.colors.text.primary;
  }
};

// Display functions
const getDisplay = (variant: MDSBubbleVariant): string => {
  switch (variant) {
    case MDSBubbleVariant.Transparent:
      return "flex";
    default:
      return "inline-block";
  }
};

// Width functions
const getMaxWidth = (variant: MDSBubbleVariant): string => {
  switch (variant) {
    case MDSBubbleVariant.Transparent:
    case MDSBubbleVariant.Image:
      return "100%";
    default:
      return "80%";
  }
};

const getWidth = (variant: MDSBubbleVariant): string => {
  switch (variant) {
    case MDSBubbleVariant.Transparent:
      return "100%";
    default:
      return "fit-content";
  }
};

// Padding functions
const getPadding = (variant: MDSBubbleVariant, theme: Theme): string => {
  switch (variant) {
    case MDSBubbleVariant.Transparent:
      return `${theme.spacing.xs} 0 0 0`;
    case MDSBubbleVariant.Image:
      return "0";
    default:
      return `${theme.spacing.smd} ${theme.spacing.md} ${theme.spacing.smd} ${theme.spacing.md}`;
  }
};

// Border radius function
const getBorderRadius = (
  variant: MDSBubbleVariant,
  isLastInGroup: boolean,
  align: MDSBubbleAlignment,
  theme: Theme
): string => {
  // Images bubbles always have a border radius, even if they
  // are the last in a group.
  if (variant === MDSBubbleVariant.Image) {
    return theme.spacing.lg;
  }

  if (!isLastInGroup || align === MDSBubbleAlignment.Center) {
    return theme.spacing.lg;
  }

  if (align === MDSBubbleAlignment.Left) {
    return `${theme.spacing.lg} ${theme.spacing.lg}  ${theme.spacing.lg} 0`;
  }

  return `${theme.spacing.lg} ${theme.spacing.lg} 0 ${theme.spacing.lg}`;
};

// Alignment function
const getAlignSelf = (align: MDSBubbleAlignment): string => {
  return align === MDSBubbleAlignment.Left
    ? "flex-start"
    : align === MDSBubbleAlignment.Center
      ? "center"
      : "flex-end";
};

// Direction function
const getFlexDirection = (align: MDSBubbleAlignment): string => {
  return align === MDSBubbleAlignment.Left ? "row" : "row-reverse";
};

const getJustifyContent = (align: MDSBubbleAlignment): string => {
  return align === MDSBubbleAlignment.Left
    ? "left"
    : align === MDSBubbleAlignment.Center
      ? "center"
      : "right";
};

const getContentGap = (variant: MDSBubbleVariant, theme: Theme): string => {
  switch (variant) {
    case MDSBubbleVariant.Transparent:
      return "0";
    default:
      return theme.spacing.sm;
  }
};

const getFontWeight = (variant: MDSBubbleVariant, theme: Theme): number => {
  switch (variant) {
    case MDSBubbleVariant.Grey:
    case MDSBubbleVariant.VoiceOthers:
    case MDSBubbleVariant.VoiceSelf:
    case MDSBubbleVariant.MessageUser:
    case MDSBubbleVariant.Transparent:
      return theme.fontWeights.regular;
    default:
      return theme.fontWeights.medium;
  }
};

const getFontSize = (variant: MDSBubbleVariant, theme: Theme): string => {
  switch (variant) {
    case MDSBubbleVariant.Grey:
      return theme.fontSizes.smedium;
    default:
      return theme.fontSizes.small;
  }
};

export const StyledBubbleWrapper = styled.div<{ align: MDSBubbleAlignment }>`
  display: flex;
  gap: 1rem;
  flex-direction: ${({ align }) => getFlexDirection(align)};

  p + p {
    margin-top: 0.75em;
  }
`;

export const StyledBubbleInner = styled.div<{
  align: MDSBubbleAlignment;
  isLastInGroup: boolean;
  $variant: MDSBubbleVariant;
}>`
  align-self: ${({ align }) => getAlignSelf(align)};
  background: ${({ $variant, theme }) => getBackgroundColor($variant, theme)};
  box-sizing: border-box;
  color: ${({ $variant, theme }) => getTextColor($variant, theme)};
  border-radius: ${({ $variant, isLastInGroup, align, theme }) =>
    getBorderRadius($variant, isLastInGroup, align, theme)};
  padding: ${({ $variant, theme }) => getPadding($variant, theme)};
  display: ${({ $variant }) => getDisplay($variant)};
  flex-direction: row;
  align-items: top;
  justify-content: ${({ align }) => getJustifyContent(align)};
  gap: ${({ theme }) => theme.spacing.smd};
  max-width: ${({ $variant }) => getMaxWidth($variant)};
  width: ${({ $variant }) => getWidth($variant)};
  font-size: ${({ $variant, theme }) => getFontSize($variant, theme)};
  line-height: ${({ theme }) => theme.lineHeights.small};
  font-weight: ${({ $variant, theme }) => getFontWeight($variant, theme)};

  transition: border-radius 0.1s ease-in-out;
`;

export const StyledContent = styled.div<{ $variant: MDSBubbleVariant }>`
  display: flex;
  flex: 1;
  min-width: 0;
  max-width: fit-content;
  flex-direction: column;
  gap: ${({ $variant, theme }) => getContentGap($variant, theme)};
`;

export const StyledLoader = styled.div<{ addSpace: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
`;
